/**
 * Elementor Frontend Handlers for Storedash Widgets
 *
 * Registers proper frontend handlers for all Storedash widgets following
 * Elementor's best practices for editor and frontend performance.
 *
 * Benefits:
 * - Better editor integration (instant preview updates)
 * - Proper handler lifecycle (init, destroy, re-init)
 * - Cleaner separation of concerns
 * - Follows Elementor's recommended architecture
 *
 * @package Storedash
 * @version 3.2.0
 * @updated 2025-10-14
 *
 * References:
 * - Elementor Developers: Frontend Handlers
 * - elementorModules.frontend.handlers.Base
 */

(function($) {
    'use strict';

    /**
     * Mini Cart Widget Handler
     *
     * Handles initialization and lifecycle of the mini cart widget
     * in both frontend and Elementor editor environments.
     */
    class StoredashMiniCartHandler extends elementorModules.frontend.handlers.Base {
        /**
         * Get default settings for the widget
         */
        getDefaultSettings() {
            return {
                selectors: {
                    trigger: '.storedash-mini-cart-trigger',
                    sidebar: '.storedash-mini-cart-sidebar',
                    overlay: '.storedash-mini-cart-overlay'
                }
            };
        }

        /**
         * Get default elements for the widget
         */
        getDefaultElements() {
            const selectors = this.getSettings('selectors');
            return {
                $trigger: this.$element.find(selectors.trigger),
                $sidebar: $(selectors.sidebar),
                $overlay: $(selectors.overlay)
            };
        }

        /**
         * Bind events - called when widget is initialized
         */
        bindEvents() {
            // Re-initialize mini cart when preset changes in editor
            if (elementorFrontend && elementorFrontend.isEditMode()) {
                this.onPresetChange();
            }
        }

        /**
         * Handle preset changes in Elementor editor
         */
        onPresetChange() {
            const self = this;

            // Listen for settings changes in editor
            if (window.elementor && window.elementor.channels) {
                elementor.channels.editor.on('change', function(view) {
                    if (view?.model?.get('widgetType') === 'storedash-mini-cart') {
                        // Re-apply preset when settings change
                        setTimeout(() => {
                            if (window.storedashMiniCart && window.storedashMiniCart.applyPreset) {
                                window.storedashMiniCart.applyPreset();
                            }
                        }, 50);
                    }
                });
            }
        }

        /**
         * Called when widget is initialized
         */
        onInit() {
            super.onInit();

            // Initialize the mini cart controller if not already initialized
            if (!window.storedashMiniCart && typeof MiniCartController !== 'undefined') {
                window.storedashMiniCart = new MiniCartController();
            }
        }

        /**
         * Called when widget is destroyed (cleanup)
         */
        onDestroy() {
            // Clean up event listeners if needed
            super.onDestroy();
        }
    }

    /**
     * Add to Cart Widget Handler
     *
     * Handles add to cart button behavior in both frontend and editor.
     */
    class StoredashAddToCartHandler extends elementorModules.frontend.handlers.Base {
        getDefaultSettings() {
            return {
                selectors: {
                    button: '.storedash-add-to-cart-button'
                }
            };
        }

        getDefaultElements() {
            const selectors = this.getSettings('selectors');
            return {
                $button: this.$element.find(selectors.button)
            };
        }

        onInit() {
            super.onInit();

            // Re-initialize add to cart handler if available
            if (window.StoredashAddToCartHandler) {
                // Handler already initialized in add-to-cart-widget.js
                // Just ensure it's bound to this widget
            }
        }
    }

    /**
     * Product Grid Widget Handler
     *
     * Handles product grid/carousel functionality in both frontend and editor.
     * Properly initializes Swiper and handles editor updates.
     */
    class StoredashProductGridHandler extends elementorModules.frontend.handlers.Base {
        getDefaultSettings() {
            return {
                selectors: {
                    wrapper: '.storedash-products-wrapper',
                    carousel: '.storedash-products-carousel',
                    grid: '.storedash-products-grid'
                }
            };
        }

        getDefaultElements() {
            const selectors = this.getSettings('selectors');
            return {
                $wrapper: this.$element.find(selectors.wrapper),
                $carousel: this.$element.find(selectors.carousel),
                $grid: this.$element.find(selectors.grid)
            };
        }

        bindEvents() {
            // In editor mode, listen for settings changes
            if (elementorFrontend && elementorFrontend.isEditMode()) {
                this.onEditorChange();
            }
        }

        /**
         * Handle editor changes - re-initialize carousel when settings change
         */
        onEditorChange() {
            const self = this;

            if (window.elementor && window.elementor.channels) {
                elementor.channels.editor.on('change', function(view) {
                    // Check if this is our widget
                    const widgetType = view?.model?.get('widgetType');
                    const elementCid = view?.model?.get('id');
                    const ourElementCid = self.$element.data('model-cid');

                    if (widgetType === 'storedash-product-grid' && elementCid === ourElementCid) {
                        
                        // Debounce re-initialization to avoid too many updates
                        clearTimeout(self.reinitTimeout);
                        self.reinitTimeout = setTimeout(() => {
                            self.destroySwiper();
                            self.init();
                        }, 300);
                    }
                });
            }
        }

        /**
         * Initialize the widget
         */
        onInit() {
            super.onInit();
            this.init();
        }

        /**
         * Main initialization method
         */
        init() {
            const settings = this.getElementSettings();
            
            // Re-query elements after content changes
            this.elements.$carousel = this.$element.find('.storedash-products-carousel');
            this.elements.$grid = this.$element.find('.storedash-products-grid');

            // Initialize carousel if display mode is carousel
            if (settings.display_mode === 'carousel' && this.elements.$carousel.length) {
                // Wait a bit for DOM to be ready
                setTimeout(() => {
                    this.initCarousel();
                }, 100);
            }
        }

        /**
         * Initialize Swiper carousel
         */
        initCarousel() {
            if (typeof Swiper === 'undefined') {
                return;
            }

            const $carousel = this.elements.$carousel;
            if (!$carousel || !$carousel.length) {
                return;
            }

            // Get settings from data attribute
            const carouselEl = $carousel[0];
            let swiperSettings = {};
            
            try {
                const dataSettings = carouselEl.dataset.settings;
                if (dataSettings) {
                    swiperSettings = JSON.parse(dataSettings);
                }
            } catch (e) {
            }

            // Destroy existing instance if any
            this.destroySwiper();

            // CRITICAL: Find navigation/pagination elements in the widget container
            // They can be either inside carousel OR in header (when integrated with heading)
            const widgetEl = this.$element[0];
            const nextButton = widgetEl.querySelector('.swiper-button-next');
            const prevButton = widgetEl.querySelector('.swiper-button-prev');
            const paginationEl = widgetEl.querySelector('.swiper-pagination');

            // Configure Swiper
            const swiperConfig = {
                slidesPerView: swiperSettings.slidesPerView || 2,
                spaceBetween: swiperSettings.spaceBetween || 20,
                loop: swiperSettings.loop || false,
                speed: 600,
                grabCursor: true,
                // Touch/Click handling - increase threshold to better distinguish clicks from swipes
                threshold: 8, // Minimum distance (px) to trigger swipe (increased from default 5px)
                autoplay: swiperSettings.autoplay || false,
                // Navigation - IMPORTANT: Scope to THIS widget instance only
                navigation: swiperSettings.navigation && nextButton && prevButton ? {
                    nextEl: nextButton,
                    prevEl: prevButton,
                } : false,
                // Pagination - IMPORTANT: Scope to THIS widget instance only
                pagination: swiperSettings.pagination && paginationEl ? {
                    el: paginationEl,
                    clickable: true,
                } : false,
                breakpoints: swiperSettings.breakpoints || {},
                watchSlidesProgress: true,
                watchSlidesVisibility: true,
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
            };

            // Initialize Swiper
            try {
                this.swiper = new Swiper(carouselEl, swiperConfig);
                
                // Track if Swiper is being dragged to prevent clicks during swipe
                this.isDragging = false;
                let touchStartX = 0;
                let touchStartY = 0;
                
                this.swiper.on('touchStart', () => {
                    this.isDragging = false;
                    if (this.swiper.touches) {
                        touchStartX = this.swiper.touches.currentX || 0;
                        touchStartY = this.swiper.touches.currentY || 0;
                    }
                });
                
                this.swiper.on('touchMove', () => {
                    if (this.swiper.touches) {
                        const deltaX = Math.abs((this.swiper.touches.currentX || 0) - touchStartX);
                        const deltaY = Math.abs((this.swiper.touches.currentY || 0) - touchStartY);
                        // If moved more than threshold, consider it a drag
                        if (deltaX > 8 || deltaY > 8) {
                            this.isDragging = true;
                        }
                    }
                });
                
                this.swiper.on('touchEnd', () => {
                    // Reset dragging state after a short delay
                    setTimeout(() => {
                        this.isDragging = false;
                    }, 100);
                });
                
                // Prevent clicks on product cards during/after swipe using event delegation
                carouselEl.addEventListener('click', (e) => {
                    const productCard = e.target.closest('.product-card');
                    if (productCard && this.isDragging) {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                }, true); // Use capture phase
            } catch (error) {
            }
        }

        /**
         * Destroy Swiper instance
         */
        destroySwiper() {
            if (this.swiper) {
                try {
                    this.swiper.destroy(true, true);
                    this.swiper = null;
                } catch (error) {
                }
            }
        }

        /**
         * Called when widget is destroyed (cleanup)
         */
        onDestroy() {
            this.destroySwiper();
            
            if (this.reinitTimeout) {
                clearTimeout(this.reinitTimeout);
            }
            
            super.onDestroy();
        }
    }

    /**
     * Register all handlers when Elementor frontend is ready
     */
    $(window).on('elementor/frontend/init', function() {
        // Register Mini Cart handler
        elementorFrontend.elementsHandler.attachHandler('storedash-mini-cart', StoredashMiniCartHandler);

        // Register Add to Cart handler
        elementorFrontend.elementsHandler.attachHandler('storedash-add-to-cart', StoredashAddToCartHandler);

        // Register Product Grid handler
        elementorFrontend.elementsHandler.attachHandler('storedash-product-grid', StoredashProductGridHandler);
    });

})(jQuery);
